import { Button, ButtonMode, Popup, Tooltip } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useEffect, useState } from 'react';
import useFetchUserPermissions from '../../../../../hooks/permissions/useFetchUserPermissions';
import { NotificationMode, RiskProductAlert } from '../../../../../models/Preference';
import { hasPermissionToRp } from '../../../../../utils/permissionsUtils';
import WatchlistAlert from '../../WatchlistAlert/WatchlistAlert';
import AlertItem from './AlertItem';
import { emailAlertOptions, hasAnyAlertSelected, riskProductAlertOptions } from './AlertPopup.logic';
import styles from './AlertPopup.module.scss';

const cx = classNames.bind(styles);

interface AlertPopupProps {
  emailAlert: NotificationMode;
  selectEmailAlert: (alert: string) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isDisabled: boolean;
  riskProductAlert: RiskProductAlert;
  selectRiskProductAlert: (alert: string) => void;
}

const AlertPopup = ({
  emailAlert,
  selectEmailAlert,
  isOpen,
  setIsOpen,
  isDisabled,
  riskProductAlert,
  selectRiskProductAlert,
}: AlertPopupProps): React.ReactElement => {
  const { data: userPermissions } = useFetchUserPermissions();
  const [emailAlertsEnabled, setEmailAlertsEnabled] = useState(emailAlert !== NotificationMode.None);
  const [rpAlertsEnabled, setRpAlertsEnabled] = useState(riskProductAlert !== RiskProductAlert.None);
  const isAnyAlertSelected = hasAnyAlertSelected(userPermissions, emailAlert, riskProductAlert);

  useEffect(() => {
    setEmailAlertsEnabled(emailAlert !== NotificationMode.None);
  }, [emailAlert, setEmailAlertsEnabled, isOpen]);

  useEffect(() => {
    setRpAlertsEnabled(riskProductAlert !== RiskProductAlert.None);
  }, [riskProductAlert, setRpAlertsEnabled, isOpen]);

  const closePopup = () => {
    if (emailAlertsEnabled) {
      setEmailAlertsEnabled(false);
    }
    if (rpAlertsEnabled) {
      setRpAlertsEnabled(false);
    }
    setIsOpen(false);
  };

  const toggleAlerts = (value: boolean) => {
    setEmailAlertsEnabled(value);

    if (value) {
      selectEmailAlert(NotificationMode.Digest);
    } else {
      selectEmailAlert(NotificationMode.None);
    }
  };

  const toggleRiskProductAlerts = (value: boolean) => {
    setRpAlertsEnabled(value);

    if (value) {
      selectRiskProductAlert(RiskProductAlert.Weekly);
    } else {
      selectRiskProductAlert(RiskProductAlert.None);
    }
  };

  const renderTriggerButton = () => (
    <div>
      <Tooltip
        trigger={
          <Button
            className={cx('popup-trigger-button')}
            dataTestId="watchlist-alert-button"
            mode={ButtonMode.TERTIARY}
            ariaLabel="Alert"
            hasEqualSidesPadding
            disabled={isDisabled}
          >
            <WatchlistAlert isAnyAlertSelected={isAnyAlertSelected} />
          </Button>
        }
        content={isAnyAlertSelected ? 'Alerts On' : 'Alerts Off'}
        disabled={isOpen}
      />
    </div>
  );

  return (
    <Popup
      className={cx('body')}
      trigger={renderTriggerButton()}
      on="click"
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={closePopup}
      position="bottom right"
      data-testid="watchlist-alert-popup"
    >
      <div className={cx('content')}>
        {userPermissions?.hasAccessToStandardAlerts() && (
          <AlertItem
            title="Research, Covenants and News"
            description="Receive email alerts when new content is published about companies in this watchlist"
            options={emailAlertOptions}
            selectedOption={emailAlert}
            alertsEnabled={emailAlertsEnabled}
            toggleAlerts={toggleAlerts}
            onOptionSelect={selectEmailAlert}
            dataTestPrefix="email"
            isDisabled={isDisabled}
          />
        )}
        {hasPermissionToRp(userPermissions) && (
          <AlertItem
            title="Risk Products"
            description="Receive email alerts when there are Risk Product changes to companies in this watchlist"
            options={riskProductAlertOptions}
            selectedOption={riskProductAlert}
            alertsEnabled={rpAlertsEnabled}
            toggleAlerts={toggleRiskProductAlerts}
            onOptionSelect={selectRiskProductAlert}
            dataTestPrefix="risk-products"
            isDisabled={isDisabled}
          />
        )}
      </div>
    </Popup>
  );
};

export default AlertPopup;
